import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "../../configurator-store";
import { TConfigurationSortField, TSortDirection } from "../../configurator-types";

const mapStateToProps = (state: RootState) => ({
  configurationSortField: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationSortField,
  configurationSortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationSortDirection
});

const ownActions = {
  setConfigurationSortField: configuratorActions.setConfigurationSortField,
  setConfigurationSortDirection: configuratorActions.setConfigurationSortDirection,
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigurationsSortOptionsBase = (props: Props) => {
  const { configurationSortField, setConfigurationSortField, configurationSortDirection, setConfigurationSortDirection, setActiveOptionsPanel } = props;

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  // Back to configuratorReducer's initialState defaults for these two fields — selections still
  // apply instantly, Reset is just a shortcut to the default combination rather than an Apply/Reset lifecycle
  const handleReset = () => {
    setConfigurationSortField("none");
    setConfigurationSortDirection("asc");
  };

  return (
    <Stack overflow="hidden" width="100%" py={2} flex={1} minHeight={0}>
      {/* Header: title + Reset + close icon — same "s" shortcut as the Products tab's Sort options, since only one is ever mounted at a time (mutually exclusive by tab) */}
      <Stack flexShrink={0} px={3} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {configuratorPageT.sortOptions}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Button color="error" t={genericTranslationsT} k={genericTranslationsK} field="reset" variant="text" size="small" onClick={handleReset} />
          <TooltipShortcut shortcut={{ action: () => setActiveOptionsPanel(null), shortCutKey: "s", modifier: "alt" }}>
            <IconButton k={genericTranslationsK.close} size="small" onClick={() => setActiveOptionsPanel(null)}>
              <DekoxIcon icon="doubleArrowRight" fontSize="small" />
            </IconButton>
          </TooltipShortcut>
        </Stack>
      </Stack>
      <Stack overflow="auto" flex={1} minHeight={0} gap={2} mt={2} px={3} py={2}>
        {/* field selector: "none" (API's own order, the default) plus name or product count */}
        <FormControl size="small" fullWidth>
          <InputLabel>{configuratorPageT.sortBy}</InputLabel>
          <Select value={configurationSortField} label={configuratorPageT.sortBy} onChange={e => setConfigurationSortField(e.target.value as TConfigurationSortField)}>
            <MenuItem value="none">{configuratorPageT.sortByNone}</MenuItem>
            <MenuItem value="configurationName">{configuratorPageT.sortByConfigurationName}</MenuItem>
            <MenuItem value="productCount">{configuratorPageT.sortByProductCount}</MenuItem>
          </Select>
        </FormControl>
        {/* direction is meaningless when no sort field is applied, so hide it entirely for "none" */}
        {configurationSortField !== "none" && (
          <FormControl size="small" fullWidth>
            <InputLabel>{configuratorPageT.sortOrder}</InputLabel>
            <Select value={configurationSortDirection} label={configuratorPageT.sortOrder} onChange={e => setConfigurationSortDirection(e.target.value as TSortDirection)}>
              <MenuItem value="asc">{genericTranslationsT.sortAscending}</MenuItem>
              <MenuItem value="desc">{genericTranslationsT.sortDescending}</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>
    </Stack>
  );
};

export const ConfigurationsSortOptions = connect(mapStateToProps, ownActions)(ConfigurationsSortOptionsBase);
